import { useRef, useState } from "react";
import { C, text } from "../theme";
import { Btn, ConfirmDialog } from "./ui";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.xlsx,.docx";
const ACCEPT_HINT = "PDF, PNG, JPG, XLSX or DOCX · up to 25 MB";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Styled upload control over a hidden native file input (UX-26): a visible
// button, format/size hints, and a busy state instead of a bare <input type=file>.
function UploadButton({ label, busy, onFile, ariaLabel }) {
  const ref = useRef(null);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, position: "relative" }}>
      <input
        ref={ref}
        type="file"
        accept={ACCEPT}
        disabled={busy}
        aria-label={ariaLabel}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, overflow: "hidden" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          onFile(file);
        }}
      />
      <Btn variant="outline" disabled={busy} onClick={() => ref.current && ref.current.click()} style={{ padding: "5px 14px", fontSize: text.sm }}>
        {busy ? "Uploading…" : label}
      </Btn>
      <span style={{ fontSize: text.xs, color: C.gray500 }}>{ACCEPT_HINT}</span>
    </span>
  );
}

export default function DocumentSection({ entityType, entityId, documents = [], checklistItems = [], checklistId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null); // document row pending confirm

  // Upload a file, optionally tagging it against a checklist slot so the
  // submission gate sees the mandatory document as satisfied.
  async function uploadFile(file, slotChecklistId) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("entityType", entityType);
      body.append("entityId", entityId);
      const cid = slotChecklistId || checklistId;
      if (cid) body.append("checklistId", cid);
      const res = await fetch("/api/documents", { method: "POST", body });
      if (!res.ok) throw new Error((await res.json()).error || "Upload failed");
      if (onChange) await onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(id) {
    const res = await fetch(`/api/documents/${id}/download`);
    if (!res.ok) return setError("Could not get download link");
    const { url } = await res.json();
    window.location = url;
  }

  async function handleDelete(d) {
    setDeleting(null);
    const res = await fetch(`/api/documents/${d.id}`, { method: "DELETE" });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return setError(body.error || "Delete failed");
    }
    setError("");
    if (onChange) await onChange();
  }

  function DocRow({ d }) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
        <button onClick={() => handleDownload(d.id)} style={{ background: "none", border: "none", color: C.info, cursor: "pointer", padding: 0 }}>
          {d.fileName}
        </button>
        <span style={{ fontSize: text.sm, color: C.gray500 }}>{fmtSize(d.fileSize)}</span>
        {d.uploadedBy?.displayName && (
          <span style={{ fontSize: text.sm, color: C.gray500 }}>· {d.uploadedBy.displayName}</span>
        )}
        <button onClick={() => setDeleting(d)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.danger, cursor: "pointer" }}>
          Delete
        </button>
      </div>
    );
  }

  const slotIds = new Set(checklistItems.map((i) => i.id));
  const docForSlot = (id) => documents.find((d) => d.checklistId === id);
  // Anything not satisfying a currently-shown slot lives under "Additional documents".
  const additionalDocs = documents.filter((d) => !d.checklistId || !slotIds.has(d.checklistId));

  return (
    <div style={{ marginTop: 16 }}>
      <ConfirmDialog
        open={!!deleting}
        title="Delete this document?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => handleDelete(deleting)}
        onCancel={() => setDeleting(null)}
      >
        {deleting ? `"${deleting.fileName}" is removed from this record.${deleting.checklistId ? " It currently satisfies a mandatory checklist item — the slot reopens and the document cannot be submitted until it is re-uploaded." : ""}` : ""}
      </ConfirmDialog>

      {checklistItems.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>Required documents</div>
          {checklistItems.map((item) => {
            const doc = docForSlot(item.id);
            const systemSatisfied = item.satisfiedBy === "VENDOR_EVALUATION";
            return (
              <div key={item.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.gray100}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: text.md, fontWeight: 600 }}>{item.name}</span>
                  {/* Why this slot exists (UX-24): show the trigger in context. */}
                  {item.condition && <span style={{ fontSize: text.xs, color: C.gray500 }}>({item.condition})</span>}
                  <span style={{ fontSize: text.xs, fontWeight: 700, color: doc || systemSatisfied ? C.success : C.danger }}>
                    {doc ? "✓ Provided" : systemSatisfied ? "✓ Satisfied by the vendor evaluation" : "Required"}
                  </span>
                </div>
                {systemSatisfied && !doc ? (
                  // The evaluation ran in this system two modules away (UX-55) —
                  // no upload demanded; the slot stays open for extras.
                  <div style={{ fontSize: text.sm, color: C.gray500, marginTop: 2 }}>
                    This PO was created from a vendor-evaluation award, which is the system's own record of the selection process. An upload here is optional.
                  </div>
                ) : null}
                {doc ? (
                  <DocRow d={doc} />
                ) : (
                  <div style={{ marginTop: 6 }}>
                    <UploadButton label="Upload file" busy={busy} ariaLabel={`Upload ${item.name}`} onFile={(f) => uploadFile(f, item.id)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 8 }}>
        {checklistItems.length > 0 ? "Additional documents" : "Documents"}
      </div>
      {additionalDocs.length === 0 && (
        <div style={{ fontSize: text.md, color: C.gray500 }}>No documents uploaded yet.</div>
      )}
      {additionalDocs.map((d) => (
        <DocRow key={d.id} d={d} />
      ))}
      <div style={{ marginTop: 8 }}>
        <UploadButton label="Upload document" busy={busy} ariaLabel="Upload document" onFile={(f) => uploadFile(f)} />
      </div>
      {error && <div role="alert" style={{ fontSize: text.sm, color: C.danger, marginTop: 4 }}>{error}</div>}
    </div>
  );
}
